// Shown when releasing a batch is refused because this run already has a BANK
// PAYMENT FILE (409 `bank_file_issued`).
//
// WHY A DIALOG AND NOT A TOAST. The two payment routes cannot see each other: the
// file leaves KaziPeople and is uploaded at the bank, while the batch pays through
// the connector. Nothing downstream reconciles them, and our item-level
// idempotency does not help because the bank never sees our references. So the
// only thing standing between "both routes used" and "everyone paid twice" is the
// person reading this. A toast they can dismiss is not that.
//
// It offers the override rather than a dead end, because the legitimate case is
// real: a file generated and then NOT uploaded. But the override costs a typed
// reason, so the decision has an author.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Textarea } from '@/features/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/features/shared/components/ui/dialog';

/** Long enough that "ok" doesn't pass, short enough not to punish a real answer. */
export const BANK_FILE_OVERRIDE_REASON_MIN = 10;

interface BankFileConflictDialogProps {
  /** Server message; dialog is open while non-null. */
  info: { message: string } | null;
  busy: boolean;
  onCancel: () => void;
  onOverride: (reason: string) => void;
}

export const BankFileConflictDialog: React.FC<BankFileConflictDialogProps> = ({
  info,
  busy,
  onCancel,
  onOverride,
}) => {
  const { t } = useTranslation('payroll');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (info) setReason('');
  }, [info]);

  const tooShort = reason.trim().length < BANK_FILE_OVERRIDE_REASON_MIN;

  return (
    <Dialog open={!!info} onOpenChange={(open) => !open && !busy && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('bankFileConflict.title', { defaultValue: 'This run already has a bank payment file' })}
          </DialogTitle>
          <DialogDescription>
            {t('bankFileConflict.body', {
              defaultValue:
                'If that file was uploaded at your bank, these employees have already been paid. Releasing here pays them a second time, and KaziPeople cannot see or undo a payment made at the bank.',
            })}
          </DialogDescription>
        </DialogHeader>

        <p className="flex items-start gap-2 rounded-lg border border-warning bg-warning-soft p-3 text-sm text-warning-strong">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {t('bankFileConflict.check', {
              defaultValue:
                'Check your bank statement before continuing. If the money already went out, close this and record it with "Already paid" instead — that makes payslips visible without paying again.',
            })}
          </span>
        </p>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {t('bankFileConflict.reasonLabel', { defaultValue: 'Why is it safe to release? (kept on the audit trail)' })}
          </p>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('bankFileConflict.reasonPh', {
              defaultValue: 'e.g. the bank file was generated but never uploaded — paying in KaziPeople instead',
            })}
          />
          {tooShort && reason.length > 0 && (
            <p className="text-xs text-danger">
              {t('bankFileConflict.reasonTooShort', {
                defaultValue: 'Please say why — a few words is enough.',
              })}
            </p>
          )}
        </div>

        <DialogFooter>
          {/* Cancel is the primary-weighted choice: not releasing is the safe
              outcome, and it is the right one in most cases that reach here. */}
          <Button variant="outline" disabled={busy} onClick={onCancel}>
            {t('bankFileConflict.cancel', { defaultValue: "Don't release" })}
          </Button>
          <Button
            variant="destructive"
            className="gap-2"
            disabled={busy || tooShort}
            onClick={() => onOverride(reason.trim())}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('bankFileConflict.confirm', { defaultValue: 'Release anyway' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BankFileConflictDialog;
